import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import Picker from 'react-month-picker';
import { format } from 'date-fns';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewListIcon from '@mui/icons-material/ViewList';

import SavedViewsPanel from './SavedViewsPanel';

// Nav bar height constant — must match MUI AppBar default (64px desktop)
const NAV_HEIGHT = 64;

// react-month-picker language config — matches changeDetails.jsx
const pickerLang = {
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  from: 'From',
  to: 'To',
};

// Format { year, month } → "Jan. 2024"
const makeMonthText = (m) => {
  if (m && m.year && m.month) return `${pickerLang.months[m.month - 1]}. ${m.year}`;
  return '?';
};

// Operator type to human-readable label
const operatorLabel = (op) => {
  switch (op) {
    case 'percentage':            return '%';
    case 'absoluteValue':         return '$';
    case 'account':               return 'Pro Forma';
    case 'calculatedAccount':     return 'Calc';
    case 'recursiveCalculatedAccount': return 'Recursive';
    default:                      return op;
  }
};

/**
 * ProjectHomeFilterDrawer
 *
 * Full-width slide-down panel below the nav bar.
 *
 * UX contract:
 *  - All filter changes are DRAFT only until Apply is clicked.
 *  - Closing without Apply discards the draft (resets to last applied values).
 *  - Clicking outside the panel closes it (and discards draft).
 *  - Apply is disabled when draft equals applied state.
 *  - The Clear button in the filter bar (outside the drawer) applies immediately.
 *
 * Props:
 *   open, onClose
 *   activeProjectInfo, activeProjectAttributes
 *   appliedFilters    — current applied activeFilters object { entityIds, deptIds, dateRange, disabledChangeIds }
 *   onApplyFilters    — (patch) => void  — merges patch into parent activeFilters
 *   monthPickerRange
 */
const ProjectHomeFilterDrawer = ({
  open,
  onClose,
  activeProjectInfo,
  activeProjectAttributes,
  appliedFilters,
  onApplyFilters,
  monthPickerRange,
  // ── Saved views props ──
  savedViews,
  savedViewsSaving,
  onSaveView,
  onSetDefaultView,
  onRenameView,
  onDeleteView,
}) => {
  // Destructure for convenience
  const appliedEntityIds    = appliedFilters?.entityIds        ?? new Set();
  const appliedDeptIds      = appliedFilters?.deptIds          ?? new Set();
  const appliedDateRange    = appliedFilters?.dateRange        ?? null;
  const appliedDisabledChangeIds = appliedFilters?.disabledChangeIds ?? new Set();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pickerRef = useRef();
  const pickerTriggerRef = useRef(); // ref on the trigger Box to measure position

  // Position the picker container relative to the trigger before opening,
  // so the fixed-position popup appears directly below the trigger button.
  const handleOpenPicker = useCallback(() => {
    pickerRef.current?.show();
    // Two rAF passes: first lets the browser apply .show and render the popup,
    // second reads the actual rendered width and clamps to the viewport.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!pickerTriggerRef.current) return;
      const rect = pickerTriggerRef.current.getBoundingClientRect();
      const drawerPicker = pickerTriggerRef.current.closest('.filter-drawer-picker');
      const container = drawerPicker?.querySelector('.rmp-container');
      const popup     = drawerPicker?.querySelector('.rmp-popup');
      if (!container) return;

      const popupWidth = popup?.offsetWidth || 670;
      const clampedLeft = Math.min(rect.left, Math.max(8, window.innerWidth - popupWidth - 8));

      container.style.setProperty('top',  `${rect.bottom + 4}px`, 'important');
      container.style.setProperty('left', `${clampedLeft}px`, 'important');
    }));
  }, []);
  const paperRef = useRef();

  // ── Nav left offset (measures main element's offsetLeft) ─────────────
  const [navLeft, setNavLeft] = useState(theme.spacing(9));
  useEffect(() => {
    const measure = () => {
      const mainEl = document.querySelector('main');
      if (mainEl && mainEl.offsetLeft > 0) setNavLeft(mainEl.offsetLeft + 'px');
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const mainEl = document.querySelector('main');
    if (observer && mainEl) observer.observe(mainEl);
    return () => observer?.disconnect();
  }, []);

  // ── Draft state — initialised from applied values when drawer opens ──
  const [draftEntityIds,          setDraftEntityIds]          = useState(() => new Set(appliedEntityIds));
  const [draftDeptIds,            setDraftDeptIds]            = useState(() => new Set(appliedDeptIds));
  const [draftDateRange,          setDraftDateRange]          = useState(appliedDateRange);
  const [draftDisabledChangeIds,  setDraftDisabledChangeIds]  = useState(() => new Set(appliedDisabledChangeIds));
  // Tracks which quarter chips are active. Key format: "YYYY-Q" (e.g. "2023-1").
  // Empty = no quarter chips selected (either full range or manual picker selection).
  const [selectedQuarters, setSelectedQuarters] = useState(() => new Set());

  // Reset draft to current applied state every time the drawer opens
  useEffect(() => {
    if (open) {
      setDraftEntityIds(new Set(appliedEntityIds));
      setDraftDeptIds(new Set(appliedDeptIds));
      setDraftDateRange(appliedDateRange);
      setDraftDisabledChangeIds(new Set(appliedDisabledChangeIds));
      setSelectedQuarters(new Set());
    }
  }, [open]); // intentionally only on open — not on every applied change // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click-outside to close ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (paperRef.current && !paperRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Use capture so we catch clicks before any stopPropagation in the grid
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, onClose]);

  // ── Dirty check — is draft different from applied? ───────────────────
  const isDirty = useMemo(() => {
    if (draftEntityIds.size !== appliedEntityIds.size) return true;
    for (const id of draftEntityIds) if (!appliedEntityIds.has(id)) return true;
    if (draftDeptIds.size !== appliedDeptIds.size) return true;
    for (const id of draftDeptIds) if (!appliedDeptIds.has(id)) return true;
    if (draftDisabledChangeIds.size !== appliedDisabledChangeIds.size) return true;
    for (const id of draftDisabledChangeIds) if (!appliedDisabledChangeIds.has(id)) return true;
    const dr = draftDateRange;
    const ar = appliedDateRange;
    if (!dr && !ar) return false;
    if (!dr || !ar) return true;
    return dr.from?.year  !== ar.from?.year  ||
           dr.from?.month !== ar.from?.month ||
           dr.to?.year    !== ar.to?.year    ||
           dr.to?.month   !== ar.to?.month;
  }, [draftEntityIds, appliedEntityIds, draftDeptIds, appliedDeptIds, draftDisabledChangeIds, appliedDisabledChangeIds, draftDateRange, appliedDateRange]);

  // ── Is the draft a "all data / no filters" state? ────────────────────
  const draftIsEmpty = useMemo(() => {
    if (draftEntityIds.size > 0) return false;
    if (draftDeptIds.size > 0)   return false;
    if (draftDisabledChangeIds.size > 0) return false;
    const isFullRange = !draftDateRange ||
      (monthPickerRange &&
        draftDateRange.from?.year  === monthPickerRange.min.year  &&
        draftDateRange.from?.month === monthPickerRange.min.month &&
        draftDateRange.to?.year    === monthPickerRange.max.year  &&
        draftDateRange.to?.month   === monthPickerRange.max.month);
    return isFullRange;
  }, [draftEntityIds, draftDeptIds, draftDisabledChangeIds, draftDateRange, monthPickerRange]);

  // ── Apply handler — commits all draft dimensions at once ─────────────
  const handleApply = useCallback(() => {
    onApplyFilters({
      entityIds:         draftEntityIds,
      deptIds:           draftDeptIds,
      dateRange:         draftDateRange,
      disabledChangeIds: draftDisabledChangeIds,
    });
    onClose();
  }, [draftEntityIds, draftDeptIds, draftDateRange, draftDisabledChangeIds, onApplyFilters, onClose]);

  // ── Load a saved view into draft state ───────────────────────────────
  // Clicking a saved view populates the draft dimensions so the user can
  // review or adjust before hitting Apply. It does NOT apply immediately.
  const handleApplyViewToDraft = useCallback((view) => {
    const fs = view.filter_state || {};
    setDraftEntityIds(Array.isArray(fs.entityIds) ? new Set(fs.entityIds) : new Set());
    setDraftDeptIds(Array.isArray(fs.deptIds) ? new Set(fs.deptIds) : new Set());
    setDraftDateRange(fs.dateRange ?? null);
    setDraftDisabledChangeIds(Array.isArray(fs.disabledChangeIds) ? new Set(fs.disabledChangeIds) : new Set());
    setSelectedQuarters(new Set());
  }, []);

  // ── Clear draft (inside drawer) ──────────────────────────────────────
  const handleClearDraft = useCallback(() => {
    setDraftEntityIds(new Set());
    setDraftDeptIds(new Set());
    setDraftDateRange(null);
    setDraftDisabledChangeIds(new Set());
    setSelectedQuarters(new Set());
  }, []);

  // ── Entity helpers ────────────────────────────────────────────────────
  const handleEntityToggle = useCallback((idStr) => {
    setDraftEntityIds(prev => {
      const next = new Set(prev);
      if (next.has(idStr)) next.delete(idStr); else next.add(idStr);
      return next;
    });
  }, []);

  // ── Department helpers ────────────────────────────────────────────────
  const handleDeptToggle = useCallback((deptKey) => {
    setDraftDeptIds(prev => {
      const next = new Set(prev);
      if (next.has(deptKey)) next.delete(deptKey); else next.add(deptKey);
      return next;
    });
  }, []);

  // ── Derived display values (declared early so helpers below can reference them) ──
  const entities = useMemo(() =>
    (activeProjectAttributes?.entities || [])
      .slice().sort((a, b) => String(a.entity_id).localeCompare(String(b.entity_id))),
    [activeProjectAttributes?.entities]);

  const departments = useMemo(() =>
    (activeProjectAttributes?.departments || [])
      .slice().sort((a, b) => {
        const ec = String(a.entityId).localeCompare(String(b.entityId));
        return ec !== 0 ? ec : String(a.departmentId).localeCompare(String(b.departmentId));
      }),
    [activeProjectAttributes?.departments]);

  const toggleableChanges = useMemo(() =>
    (activeProjectAttributes?.changes || []).filter(
      c => c.function_operator !== 'calculatedAccount' &&
           c.function_operator !== 'recursiveCalculatedAccount'
    ),
    [activeProjectAttributes?.changes]);

  const deptsByEntity = useMemo(() =>
    departments.reduce((acc, dept) => {
      const key = dept.entityId;
      if (!acc[key]) acc[key] = { entityId: dept.entityId, entityName: dept.entityName, depts: [] };
      acc[key].depts.push(dept);
      return acc;
    }, {}),
    [departments]);

  // When entity draft changes, drop any selected depts that belong to
  // unselected entities so the state stays consistent.
  useEffect(() => {
    if (draftEntityIds.size === 0) return; // no entity filter — all depts valid
    setDraftDeptIds(prev => {
      if (prev.size === 0) return prev;
      const entityByDept = new Map(
        departments.map(d => [`${d.departmentId} - ${d.departmentName}`, String(d.entityId)])
      );
      const next = new Set([...prev].filter(dk => {
        const eid = entityByDept.get(dk);
        return eid == null || draftEntityIds.has(eid);
      }));
      return next.size === prev.size ? prev : next;
    });
  }, [draftEntityIds, departments]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Date range helpers ────────────────────────────────────────────────
  const handleMonthPickerDismiss = useCallback((value) => {
    if (!value) return;
    const startDate = format(new Date(value.from.year, value.from.month - 1, 1), 'yyyy-MM-dd');
    const endDate   = format(new Date(value.to.year,   value.to.month   - 1, 1), 'yyyy-MM-dd');
    setDraftDateRange({ ...value, startDate, endDate });
    setSelectedQuarters(new Set()); // manual picker selection clears quarter chips
  }, []);

  const effectiveDraftDateRange = draftDateRange ?? (monthPickerRange ? { from: monthPickerRange.min, to: monthPickerRange.max } : null);

  const isDraftFullRange = !draftDateRange ||
    (monthPickerRange &&
      effectiveDraftDateRange?.from?.year  === monthPickerRange.min.year  &&
      effectiveDraftDateRange?.from?.month === monthPickerRange.min.month &&
      effectiveDraftDateRange?.to?.year    === monthPickerRange.max.year  &&
      effectiveDraftDateRange?.to?.month   === monthPickerRange.max.month);

  const appliedActiveCount =
    appliedEntityIds.size + appliedDeptIds.size +
    (appliedDateRange ? 1 : 0) + appliedDisabledChangeIds.size;

  const drawerMaxHeight = isMobile ? '85vh' : '70vh';

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit timeout={220}>
      <Paper
        ref={paperRef}
        elevation={8}
        square
        sx={{
          position: 'fixed',
          top: NAV_HEIGHT,
          left: navLeft,
          right: 0,
          zIndex: 1198,
          maxHeight: drawerMaxHeight,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          borderTop: '1px solid',
          borderColor: 'divider',
          background: '#fff',
        }}
      >
        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>

        {/* ── Header row ─────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
              Filters &amp; Columns
            </Typography>
            {/* Applied filter summary chips — shows what's currently active */}
            {appliedEntityIds.size > 0 && (
              <Chip size="small" label={`${appliedEntityIds.size} entit${appliedEntityIds.size === 1 ? 'y' : 'ies'}`}
                color="primary" variant="outlined"
                sx={{ fontSize: 10, height: 20, '& .MuiChip-label': { px: 0.75 } }} />
            )}
            {appliedDeptIds.size > 0 && (
              <Chip size="small" label={`${appliedDeptIds.size} dept${appliedDeptIds.size === 1 ? '' : 's'}`}
                color="secondary" variant="outlined"
                sx={{ fontSize: 10, height: 20, '& .MuiChip-label': { px: 0.75 } }} />
            )}
            {appliedDateRange && (
              <Chip size="small" label={`${makeMonthText(effectiveDraftDateRange?.from)} – ${makeMonthText(effectiveDraftDateRange?.to)}`}
                color="info" variant="outlined"
                sx={{ fontSize: 10, height: 20, '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Box>
          <Tooltip title="Close (discard changes)">
            <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Filter body — 4-column grid on desktop ──────────────────── */}
        <Grid container spacing={0} sx={{ minHeight: 0 }}>
          {/* ── 1. Entity ─────────────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={3}
            sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}
          >
            <SectionHeader
              icon={<BusinessIcon fontSize="small" />}
              title="Entity"
              activeCount={draftEntityIds.size}
              onClear={() => setDraftEntityIds(new Set())}
            />
            <Box sx={{ px: 1.5, pb: 1.5, pt: 1, maxHeight: { xs: 180, md: 260 }, overflowY: 'auto' }}>
              <FilterRow checked={draftEntityIds.size === 0} label="All Entities"
                bold={draftEntityIds.size === 0} onChange={() => setDraftEntityIds(new Set())} />
              <Divider sx={{ my: 0.5 }} />
              {entities.map((entity) => {
                const idStr = String(entity.entity_id);
                return (
                  <FilterRow key={idStr} checked={draftEntityIds.has(idStr)}
                    label={`${idStr} - ${entity.entity_name}`}
                    onChange={() => handleEntityToggle(idStr)} />
                );
              })}
            </Box>
          </Grid>

          {/* ── 2. Department ─────────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={3}
            sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}
          >
            <SectionHeader
              icon={<AccountTreeIcon fontSize="small" />}
              title="Department"
              activeCount={draftDeptIds.size}
              onClear={() => setDraftDeptIds(new Set())}
            />
            <Box sx={{ px: 1.5, pb: 1.5, pt: 1, maxHeight: { xs: 180, md: 260 }, overflowY: 'auto' }}>
              <FilterRow checked={draftDeptIds.size === 0} label="All Departments"
                bold={draftDeptIds.size === 0} onChange={() => setDraftDeptIds(new Set())} />
              <Divider sx={{ my: 0.5 }} />
              {Object.values(deptsByEntity).map(({ entityId, entityName, depts }) => {
                // When an entity filter is active, departments for unselected entities are disabled
                const entityActive = draftEntityIds.size === 0 || draftEntityIds.has(String(entityId));
                return (
                  <Box key={entityId} sx={{ opacity: entityActive ? 1 : 0.35, pointerEvents: entityActive ? 'auto' : 'none' }}>
                    <Typography variant="caption" sx={{
                      display: 'block', px: 0.5, pt: 0.75, pb: 0.25,
                      fontWeight: 600, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10,
                    }}>
                      {entityName || entityId}
                    </Typography>
                    {depts.map((dept) => {
                      const deptKey = `${dept.departmentId} - ${dept.departmentName}`;
                      return (
                        <FilterRow key={deptKey} checked={draftDeptIds.has(deptKey)}
                          label={deptKey} onChange={() => handleDeptToggle(deptKey)} indent />
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* ── 3. Date Range ─────────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={3}
            sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}
          >
            <SectionHeader
              icon={<DateRangeIcon fontSize="small" />}
              title="Date Range"
              activeCount={!isDraftFullRange ? 1 : 0}
              onClear={!isDraftFullRange ? () => setDraftDateRange(null) : null}
            />
            <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
              {monthPickerRange && effectiveDraftDateRange && (
                <Box className="filter-drawer-picker">
                  <Picker
                    key={`${effectiveDraftDateRange.from?.year}-${effectiveDraftDateRange.from?.month}-${effectiveDraftDateRange.to?.year}-${effectiveDraftDateRange.to?.month}`}
                    ref={pickerRef}
                    years={monthPickerRange}
                    value={effectiveDraftDateRange}
                    lang={pickerLang}
                    theme="light"
                    onChange={() => {}}
                    onDismiss={handleMonthPickerDismiss}
                  >
                    <Box
                      ref={pickerTriggerRef}
                      className="month-range-input"
                      onClick={handleOpenPicker}
                      sx={{
                        cursor: 'pointer', px: 1.5, py: 1,
                        border: '1px solid',
                        borderColor: !isDraftFullRange ? 'info.main' : 'divider',
                        borderRadius: 1,
                        background: !isDraftFullRange ? 'rgba(2,136,209,0.04)' : 'transparent',
                        display: 'flex', alignItems: 'center', gap: 1,
                        width: '100%',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.15s',
                        '&:hover': { borderColor: 'text.primary' },
                      }}
                    >
                      <DateRangeIcon fontSize="small"
                        sx={{ color: !isDraftFullRange ? 'info.main' : 'action.active', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: !isDraftFullRange ? 600 : 400 }}>
                        {makeMonthText(effectiveDraftDateRange.from)} &ndash; {makeMonthText(effectiveDraftDateRange.to)}
                      </Typography>
                    </Box>
                  </Picker>
                </Box>
              )}

              {monthPickerRange && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                  {buildQuarterChips(monthPickerRange, selectedQuarters, (quarterKey, from, to, quarters) => {
                    setSelectedQuarters(quarters);
                    // Derive draftDateRange as the union of all selected quarters
                    if (quarters.size === 0) {
                      setDraftDateRange(null);
                    } else {
                      // Collect all selected quarter {from, to} bounds and take min/max
                      // We re-derive from the pickerRange by parsing keys "YYYY-Q"
                      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      let minYear = Infinity, minMonth = Infinity, maxYear = -Infinity, maxMonth = -Infinity;
                      quarters.forEach(key => {
                        const [y, q] = key.split('-').map(Number);
                        const qStartMonth = (q - 1) * 3 + 1;
                        const qEndMonth   = q * 3;
                        // Clamp to project bounds
                        const clampedFrom = {
                          year: y,
                          month: y === monthPickerRange.min.year
                            ? Math.max(qStartMonth, monthPickerRange.min.month)
                            : qStartMonth,
                        };
                        const clampedTo = {
                          year: y,
                          month: y === monthPickerRange.max.year
                            ? Math.min(qEndMonth, monthPickerRange.max.month)
                            : qEndMonth,
                        };
                        const fromNum = clampedFrom.year * 100 + clampedFrom.month;
                        const toNum   = clampedTo.year   * 100 + clampedTo.month;
                        if (fromNum < minYear * 100 + minMonth) { minYear = clampedFrom.year; minMonth = clampedFrom.month; }
                        if (toNum   > maxYear * 100 + maxMonth) { maxYear = clampedTo.year;   maxMonth = clampedTo.month; }
                      });
                      const startDate = format(new Date(minYear, minMonth - 1, 1), 'yyyy-MM-dd');
                      const endDate   = format(new Date(maxYear, maxMonth - 1, 1), 'yyyy-MM-dd');
                      setDraftDateRange({
                        from: { year: minYear, month: minMonth },
                        to:   { year: maxYear, month: maxMonth },
                        startDate,
                        endDate,
                      });
                    }
                  })}
                  <Chip label="Full range" size="small"
                    variant={isDraftFullRange ? 'filled' : 'outlined'}
                    color={isDraftFullRange ? 'primary' : 'default'}
                    onClick={() => { setDraftDateRange(null); setSelectedQuarters(new Set()); }}
                    sx={{ fontSize: 11 }} />
                </Box>
              )}
            </Box>
          </Grid>

          {/* ── 4. Changes ────────────────────────────────────────────── */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionHeader
              icon={<TimelineIcon fontSize="small" />}
              title="Changes"
              activeCount={draftDisabledChangeIds.size > 0 ? draftDisabledChangeIds.size : 0}
              activeLabel={draftDisabledChangeIds.size > 0 ? `${draftDisabledChangeIds.size} hidden` : null}
              onClear={draftDisabledChangeIds.size > 0
                ? () => setDraftDisabledChangeIds(new Set())
                : null}
            />
            <Box sx={{ px: 1.5, pb: 1.5, pt: 1, maxHeight: { xs: 180, md: 260 }, overflowY: 'auto' }}>
              {toggleableChanges.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', py: 1 }}>
                  No changes added yet
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Button size="small" variant="text"
                      sx={{ fontSize: 11, py: 0, minWidth: 0, color: 'text.secondary' }}
                      onClick={() => setDraftDisabledChangeIds(new Set())}>
                      Show all
                    </Button>
                    <Button size="small" variant="text"
                      sx={{ fontSize: 11, py: 0, minWidth: 0, color: 'text.secondary' }}
                      onClick={() => setDraftDisabledChangeIds(new Set(toggleableChanges.map(c => c.id)))}>
                      Hide all
                    </Button>
                  </Box>
                  <List dense disablePadding>
                    {toggleableChanges.map((change) => {
                      const visible = !draftDisabledChangeIds.has(change.id);
                      return (
                        <ListItem key={change.id} disablePadding
                          sx={{ py: 0.25, opacity: visible ? 1 : 0.45, transition: 'opacity 0.15s' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.75 }}>
                            <Switch size="small" checked={visible}
                              onChange={(e) => {
                                const next = new Set(draftDisabledChangeIds);
                                if (e.target.checked) next.delete(change.id);
                                else next.add(change.id);
                                setDraftDisabledChangeIds(next);
                              }}
                              sx={{ flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" sx={{
                                lineHeight: 1.3, fontWeight: visible ? 500 : 400,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {change.change_label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                                {operatorLabel(change.function_operator)}
                                {change.operator_value != null ? ` · ${change.operator_value}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        </Box>{/* end scrollable body */}

        {/* ── Saved filter views — collapsible accordion ───────────────── */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            '&:before': { display: 'none' },
            background: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize="small" />}
            sx={{
              px: { xs: 2, sm: 3 },
              py: 0,
              minHeight: 40,
              '& .MuiAccordionSummary-content': { my: 0.75 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ViewListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: 12 }}>
                Saved Filter Views
              </Typography>
              {savedViews?.length > 0 && (
                <Chip
                  size="small"
                  label={savedViews.length}
                  sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <SavedViewsPanel
              views={savedViews || []}
              saving={savedViewsSaving}
              onApplyView={handleApplyViewToDraft}
              onSaveView={onSaveView}
              onSetDefault={onSetDefaultView}
              onRenameView={onRenameView}
              onDeleteView={onDeleteView}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── Sticky footer — Apply / Clear ────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            px: { xs: 2, sm: 3 },
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            background: '#fff',
            flexShrink: 0,
          }}
        >
          {isDirty && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mr: 'auto' }}>
              Unapplied filters
            </Typography>
          )}
          <Button
            size="small"
            variant="text"
            disabled={draftIsEmpty && appliedActiveCount === 0}
            onClick={handleClearDraft}
            sx={{ color: 'text.secondary', fontSize: 13 }}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!isDirty}
            onClick={handleApply}
            disableElevation
            sx={{ fontSize: 13, px: 2.5 }}
          >
            Apply
          </Button>
        </Box>
      </Paper>
    </Slide>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────

/** Section header with icon, title, active badge, and optional clear button */
const SectionHeader = ({ icon, title, activeCount, activeLabel, onClear }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      position: 'sticky',
      top: 0,
      background: '#fff',
      zIndex: 1,
    }}
  >
    <Box sx={{ color: activeCount > 0 ? 'primary.main' : 'action.active', display: 'flex' }}>
      {icon}
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, flex: 1, fontSize: 11 }}>
      {title}
    </Typography>
    {activeCount > 0 && (
      <Chip
        label={activeLabel || activeCount}
        size="small"
        color="primary"
        sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
      />
    )}
    {onClear && (
      <Button
        size="small"
        variant="text"
        onClick={onClear}
        sx={{ fontSize: 11, py: 0, px: 0.5, minWidth: 0, color: 'text.secondary' }}
      >
        Clear
      </Button>
    )}
  </Box>
);

/** Single checkbox row used in Entity and Department lists */
const FilterRow = ({ checked, label, onChange, bold, indent }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={onChange}
        size="small"
        sx={{ py: 0.25, pl: indent ? 1.5 : 0.5 }}
      />
    }
    label={
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 600 : 400,
          fontSize: 13,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    }
    sx={{ width: '100%', mx: 0, alignItems: 'center', py: 0.1 }}
  />
);

/**
 * Builds Q1–Q4 quick-select chips with multi-select support.
 * Only emits chips for quarters that overlap with the project range.
 *
 * @param {object} pickerRange   - { min: {year, month}, max: {year, month} }
 * @param {Set<string>} selectedQuarters - Set of active quarter keys "YYYY-Q"
 * @param {function} onToggle    - (quarterKey, from, to, nextSet) => void
 */
const buildQuarterChips = (pickerRange, selectedQuarters, onToggle) => {
  if (!pickerRange?.min || !pickerRange?.max) return null;

  const quarterColors = [
    'rgba(59,130,246,0.15)',
    'rgba(16,185,129,0.15)',
    'rgba(245,158,11,0.15)',
    'rgba(239,68,68,0.15)',
  ];
  const quarterBorders = [
    'rgba(59,130,246,0.5)',
    'rgba(16,185,129,0.5)',
    'rgba(245,158,11,0.5)',
    'rgba(239,68,68,0.5)',
  ];

  const chips = [];
  const startYear = pickerRange.min.year;
  const endYear   = pickerRange.max.year;

  for (let year = startYear; year <= endYear; year++) {
    for (let q = 0; q < 4; q++) {
      const qStartMonth = q * 3 + 1;   // 1, 4, 7, 10
      const qEndMonth   = q * 3 + 3;   // 3, 6, 9, 12

      // Only show if quarter overlaps with the project range
      const afterMin =
        year > pickerRange.min.year ||
        (year === pickerRange.min.year && qEndMonth >= pickerRange.min.month);
      const beforeMax =
        year < pickerRange.max.year ||
        (year === pickerRange.max.year && qStartMonth <= pickerRange.max.month);
      if (!afterMin || !beforeMax) continue;

      // Clamp to project bounds
      const from = {
        year,
        month: year === pickerRange.min.year ? Math.max(qStartMonth, pickerRange.min.month) : qStartMonth,
      };
      const to = {
        year,
        month: year === pickerRange.max.year ? Math.min(qEndMonth, pickerRange.max.month) : qEndMonth,
      };

      const quarterKey = `${year}-${q + 1}`;
      const isActive = selectedQuarters.has(quarterKey);
      const label = endYear > startYear ? `Q${q + 1} ${year}` : `Q${q + 1}`;

      chips.push(
        <Chip
          key={quarterKey}
          label={label}
          size="small"
          variant={isActive ? 'filled' : 'outlined'}
          onClick={() => {
            const next = new Set(selectedQuarters);
            if (next.has(quarterKey)) next.delete(quarterKey);
            else next.add(quarterKey);
            onToggle(quarterKey, from, to, next);
          }}
          sx={{
            fontSize: 11,
            background: isActive ? quarterColors[q] : 'transparent',
            borderColor: isActive ? quarterBorders[q] : undefined,
            color: isActive ? 'text.primary' : 'text.secondary',
            '&:hover': { background: quarterColors[q] },
          }}
        />
      );
    }
  }

  return chips;
};

export default ProjectHomeFilterDrawer;
